import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
  Zoom,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import colors from '@/styles/colors'
import { CBox, RBox } from '@/pages/__styled'
import { RoundedFilledButton, RoundedOutlineButton } from '@/components/StyledComponents'
import { OddlyouNoteType, PrivacyType, TopicType } from '@/utils/constants'
import {
  useAddNoteTopic,
  useGetTopicDetails,
  useGetTopics,
  useUploadMedia,
} from '@/hooks/api/dashboard'
import { ModernCreateModal } from '@/components'
import { Topic } from '@/types'
import { getPlainNoteTextFromNote } from '@/utils/helper'
import DoneIcon from '@mui/icons-material/Done'
import { toast } from 'react-hot-toast'
import FullScreenLoadingView from './FullScreenLoadingView'

interface SaveScreenshotModalProps {
  file: File
  open: boolean
  onClose: () => void
}

const CustomBackdrop = styled('div')({
  backdropFilter: 'blur(8px)',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
})

const SaveScreenshotModal = ({ file, open, onClose }: SaveScreenshotModalProps) => {
  const theme = useTheme()
  const lessThanMedium = useMediaQuery(theme.breakpoints.down('md'))

  const getTopics = useGetTopics()
  const uploadPhoto = useUploadMedia()
  const createTopicNote = useAddNoteTopic()
  const getTopicDetails = useGetTopicDetails()
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedTopicID, setSelectedTopicID] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [topics, setTopics] = useState<Topic[]>([])

  useEffect(() => {
    loadTopics()
  }, [])

  useEffect(() => {
    if (getTopics.data) {
      const allData: Topic[] = getTopics.data || []
      let filtered = allData.filter((e) => e.topicType == TopicType.ALBUM.toString())
      setTopics(filtered)
    }
  }, [getTopics.data])

  const selectedTopicTitle = useMemo(() => {
    if (selectedTopicID && topics.length > 0) {
      const selectedTopic = topics.find((item) => item.topicID == selectedTopicID)
      return selectedTopic ? getPlainNoteTextFromNote(selectedTopic.firstNote) : ''
    }
    return ''
  }, [selectedTopicID, topics])

  const blobURL = useMemo(() => {
    if (file) {
      return URL.createObjectURL(file)
    }
    return null
  }, [file])

  const loadTopics = (pageNo: number = 1) => {
    getTopics.mutate({
      pageNo,
      interaction: Number(PrivacyType.PRIVATE),
      topicType: Number(TopicType.ALBUM),
    })
  }

  const showNoResultView = useMemo(() => {
    return getTopics.isSuccess && getTopics.data.length == 0 && currentPage === 1
  }, [getTopics.isSuccess, getTopics.data])

  const onClickSave = async () => {
    setLoading(true)
    const fileInfo = await uploadPhoto.mutateAsync(file)
    const egoList = [
      {
        sequenceID: '1',
        egoID: fileInfo.egoID,
        photoAlbumURL: fileInfo.egoAlbumURL,
        photoFullURL: fileInfo.egoImageURL,
      },
    ]

    const noteText = {
      text: `<p>ScreenShot</p>`,
      attachments: [],
    }

    try {
      await createTopicNote.mutateAsync({
        noteName: 'Screenshot',
        topicID: selectedTopicID!,
        noteText: JSON.stringify(noteText),
        egoItems: egoList,
        noteType: OddlyouNoteType.IMAGE.toString(),
      })
      setLoading(false)
      toast.success('Screenshot Saved.')
      onClose()
    } catch (e: any) {
      setLoading(false)
      toast.error(e.toString())
    }
  }

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false)
    // Reload albums list to get any newly created albums
    loadTopics(1)
  }

  const renderTopicRowItem = (topic: Topic, isSelected: boolean) => (
    <RBox
      key={`${topic.topicID}`}
      sx={{
        justifyContent: 'space-between',
        cursor: 'pointer',
        borderRadius: '8px',
        p: '12px 14px',
        backgroundColor: isSelected ? '#ffffff' : 'transparent',
        border: isSelected ? '1px solid #6366f1' : '1px solid transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#ffffff',
          borderColor: isSelected ? '#6366f1' : '#e2e8f0',
        },
      }}
      onClick={() => {
        setSelectedTopicID(topic.topicID)
      }}
    >
      <Typography
        fontSize={14}
        fontWeight={500}
        color={isSelected ? '#6366f1' : '#1a1a1a'}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >{`${getPlainNoteTextFromNote(topic.firstNote)}`}</Typography>
      {isSelected && <DoneIcon sx={{ color: '#6366f1', fontSize: 20 }} />}
    </RBox>
  )

  return (
    <Dialog
      open={open}
      TransitionComponent={Zoom}
      onClose={onClose}
      aria-describedby='alert-dialog-slide-description'
      maxWidth='lg'
      fullScreen={lessThanMedium}
      slotProps={{
        backdrop: {
          components: {
            Root: CustomBackdrop,
          },
        },
      }}
      PaperProps={{
        sx: lessThanMedium
          ? {}
          : {
              height: '85%',
              width: '85%',
              maxWidth: '1400px',
              borderRadius: '16px',
              overflow: 'hidden',
            },
      }}
    >
      <Box
        display={'flex'}
        flexDirection={lessThanMedium ? 'column' : 'row'}
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          backgroundColor: '#ffffff',
          gap: 0,
        }}
      >
        <CBox
          sx={{
            display: lessThanMedium ? 'none' : 'flex',
            height: '100%',
            width: '280px',
            px: '20px',
            py: '24px',
            backgroundColor: '#f8f9fa',
            borderRight: '1px solid #e2e8f0',
          }}
        >
          {!showNoResultView && (
            <Typography
              sx={{
                ml: '4px',
                mb: '16px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#1a1a1a',
              }}
            >
              {'Select Album'}
            </Typography>
          )}

          <Box
            flex={1}
            sx={{
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            {(getTopicDetails.isLoading || (getTopics.isLoading && currentPage === 1)) && (
              <Box mt={1} display='flex' justifyContent={'center'}>
                <CircularProgress />
              </Box>
            )}

            {showNoResultView ? (
              <Box p={5} display='flex' justifyContent={'center'}>
                {'No Albums'}
              </Box>
            ) : (
              <>{topics.map((e, i) => renderTopicRowItem(e, e.topicID == selectedTopicID))}</>
            )}
          </Box>
          <Button
            variant='outlined'
            fullWidth
            onClick={() => setShowCreateDialog(true)}
            sx={{
              mt: '12px',
              py: '10px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                backgroundColor: '#f8fafc',
              },
            }}
          >
            {'+ Create New Album'}
          </Button>
        </CBox>
        <CBox sx={{ flex: 1 }}>
          {/* <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: lessThanMedium ? 0 : '240px',
              bottom: 0,
              right: 0,
              backgroundImage: blobURL ? `url(${blobURL})` : '',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(90px)',
            }}
          /> */}
          <RBox
            sx={{
              display: lessThanMedium ? 'flex' : 'none',
              px: '30px',
              pt: '18px',
              justifyContent: 'space-between',
            }}
          >
            {!showNoResultView && (
              <RBox>
                <Typography sx={{ mr: '4px' }}>{'Select Album:'}</Typography>
                <Select
                  defaultValue='Select'
                  value={selectedTopicTitle || ''}
                  onChange={(event: SelectChangeEvent) => {
                    const value = event.target.value
                    setSelectedTopicID(value)
                  }}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <>{`-Select-`}</>
                    }
                    return selected
                  }}
                  sx={{
                    height: 34,
                    borderRadius: 100,
                    color: '#525E9C',
                    border: '1px solid #E7EDF3',
                    minWidth: '100px',
                    maxWidth: '150px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {topics.map((item, index) => {
                    const isSelected = item.topicID == selectedTopicID
                    return (
                      <MenuItem
                        value={item.topicID}
                        key={`${index}`}
                        sx={{
                          display: 'flex',
                          flex: 1,
                          color: isSelected ? colors.meshdomBlue : colors.themeBlack,
                        }}
                      >
                        {`${getPlainNoteTextFromNote(item.firstNote)}`}
                      </MenuItem>
                    )
                  })}
                </Select>
              </RBox>
            )}
            <RoundedOutlineButton
              sx={{ px: '12px', py: '4px' }}
              onClick={() => setShowCreateDialog(true)}
            >
              {'+ Create New Album'}
            </RoundedOutlineButton>
          </RBox>
          <Box
            display={'flex'}
            flex={1}
            borderRadius={'12px'}
            sx={{
              backgroundColor: '#000000',
              zIndex: 10,
              m: lessThanMedium ? 2 : 4,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: 1,
                backgroundImage: blobURL ? `url(${blobURL})` : '',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              px: lessThanMedium ? 2 : 4,
              pb: lessThanMedium ? 2 : 4,
            }}
          >
            <Button
              variant='outlined'
              onClick={onClose}
              sx={{
                px: 4,
                py: '10px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              {'Cancel'}
            </Button>
            <Button
              variant='contained'
              disabled={selectedTopicID == null}
              onClick={onClickSave}
              sx={{
                px: 4,
                py: '10px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: '#1a1a1a',
                color: '#ffffff',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#2d2d2d',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: '#e2e8f0',
                  color: '#9ca3af',
                },
              }}
            >
              {'Save to Album'}
            </Button>
          </Box>
        </CBox>
      </Box>
      {loading && <FullScreenLoadingView />}
      <ModernCreateModal open={showCreateDialog} onClose={handleCloseCreateDialog} />
    </Dialog>
  )
}

export default SaveScreenshotModal
